import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useCallback, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { useAddVulOnMapModalStore } from './AddVulOnMapModalStore';
import { CategoryValues, CriticalityValues } from './types';

import { MLInput } from 'components/Input/Input';
import { MLSelect } from 'components/Select/Select';
import { vulnerabilityCreateSchema, VulnerabilityCreateFormValues } from 'api/InteractiveMap/types';
import useGetDataToModalVulnerabilityUser from 'hooks/InteractiveMap/useGetDataToModalVulnerabilityUser';
import usePostVulOnMap from 'hooks/InteractiveMap/usePostVulOnMap';

interface AddVulOnMapModalProps {
  refetchVul: () => void;
}

const AddVulOnMapModal: React.FC<AddVulOnMapModalProps> = ({ refetchVul }) => {
  const { isOpen, onManageAddVulOnMapClose, objectSlug, planSlug } = useAddVulOnMapModalStore();
  const { t } = useTranslation();
  const { objectUsers } = useGetDataToModalVulnerabilityUser(objectSlug);
  const { createVul } = usePostVulOnMap();
  const CATEGORIES = [
    {
      value: CategoryValues.SANITARY,
      label: t('MapModals.AddVul.categories.sanitary'),
    },
    {
      value: CategoryValues.TECHNICAL,
      label: t('MapModals.AddVul.categories.technical'),
    },
    {
      value: CategoryValues.STAFF,
      label: t('MapModals.AddVul.categories.staff'),
    },
    {
      value: CategoryValues.ENVIRONMENTAL,
      label: t('MapModals.AddVul.categories.environmental'),
    },
  ];

  const CRITICALITY = [
    {
      value: CriticalityValues.CRITICAL,
      label: t('MapModals.AddVul.criticalities.critical'),
    },
    {
      value: CriticalityValues.HIGH,
      label: t('MapModals.AddVul.criticalities.high'),
    },
    {
      value: CriticalityValues.MEDIUM,
      label: t('MapModals.AddVul.criticalities.medium'),
    },
    {
      value: CriticalityValues.LOW,
      label: t('MapModals.AddVul.criticalities.low'),
    },
  ];

  const { control, handleSubmit, reset, setValue } = useForm<VulnerabilityCreateFormValues>({
    resolver: zodResolver(vulnerabilityCreateSchema),
    defaultValues: {
      create_date: '',
      category: '',
      criticality: '',
      discovered: '',
      responsible: '',
      comment: '',
      x_coord: 0,
      y_coord: 0,
      floor_plan_slug: planSlug,
      before_photos: [],
    },
  });

  const onReset = useCallback(() => {
    reset();
    refetchVul();
    onManageAddVulOnMapClose();
  }, [reset, refetchVul, onManageAddVulOnMapClose]);

  useEffect(() => {
    if (planSlug) {
      setValue('floor_plan_slug', planSlug);
    }
  }, [planSlug, setValue]);

  const objectUserDiscovered = useMemo(() => {
    if (!objectUsers) return [];
    const defaultOption = { value: '', label: t('MapModals.AddVul.select-discovered') };

    return [
      defaultOption,
      ...objectUsers.map(user => ({
        value: String(user.id),
        label: user.last_name ? `${user.last_name} ${user.first_name}` : user.email,
      })),
    ];
  }, [objectUsers, t]);
  const objectUserResponsible = useMemo(() => {
    if (!objectUsers) return [];
    const defaultOption = { value: '', label: t('MapModals.AddVul.select-responsible') };

    return [
      defaultOption,
      ...objectUsers.map(user => ({
        value: String(user.id),
        label: user.last_name ? `${user.last_name} ${user.first_name}` : user.email,
      })),
    ];
  }, [objectUsers, t]);
  if (!isOpen) return null;
  const onSubmitForm = (data: VulnerabilityCreateFormValues) => {
    createVul(
      { vul: data, objectSlug, floorPlan: planSlug },
      {
        onSuccess: () => {
          onReset();
        },
        onError: () => {
          enqueueSnackbar(t('MapModals.AddVul.error'), { variant: 'error' });
        },
      },
    );
  };
  return (
    <>
      {/* Backdrop */}
      <Modal size="lg" backdrop="static" keyboard={false} isOpen={isOpen} toggle={onReset}>
        <ModalHeader className="border-bottom py-3 " toggle={onReset}>
          <div className="d-flex justify-content-between align-items-center w-100 gap-5 text-purple">
            {t('MapModals.AddVul.title')}
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="row mb-3">
            <div className="col-xl-6">
              <Controller
                control={control}
                name="category"
                rules={{ required: t('MapModals.AddVul.required') }}
                render={({ field, fieldState: { error } }) => (
                  <MLSelect
                    required
                    {...field}
                    label={t('MapModals.AddVul.selectCategory')}
                    value={field.value}
                    onChange={field.onChange}
                    options={CATEGORIES}
                    error={error?.message}
                  />
                )}
              />
            </div>
            <div className="col-xl-6">
              <Controller
                control={control}
                rules={{ required: t('MapModals.AddVul.required') }}
                name="criticality"
                render={({ field, fieldState: { error } }) => (
                  <MLSelect
                    required
                    {...field}
                    label={t('MapModals.AddVul.selectCriticality')}
                    value={field.value}
                    onChange={field.onChange}
                    options={CRITICALITY}
                    error={error?.message}
                  />
                )}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-xl-6">
              <Controller
                control={control}
                name="discovered"
                render={({ field, fieldState: { error } }) => (
                  <MLSelect
                    required
                    {...field}
                    value={field.value}
                    onChange={field.onChange}
                    options={objectUserDiscovered}
                    label={t('MapModals.AddVul.discovered')}
                    error={error?.message}
                  />
                )}
              />
            </div>
            <div className="col-xl-6" title={t('MapModals.AddVul.responsible-tooltip')}>
              <Controller
                control={control}
                name="responsible"
                render={({ field, fieldState: { error } }) => (
                  <MLSelect
                    required
                    {...field}
                    error={error?.message}
                    label={t('MapModals.AddVul.responsible')}
                    value={field.value}
                    onChange={field.onChange}
                    options={objectUserResponsible}
                  />
                )}
              />
            </div>
          </div>
          <div className="row mb-3">
            <div className="col-xl-12">
              <Controller
                control={control}
                name="create_date"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    {...field}
                    type="datetime-local"
                    error={error?.message}
                    label={t('MapModals.AddVul.dataTime')}
                    required
                    value={field.value ? field.value.substring(0, 16) : ''}
                    onChange={e => field.onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-xl-12">
              <Controller
                control={control}
                name="comment"
                render={({ field, fieldState: { error } }) => (
                  <MLInput
                    {...field}
                    error={error?.message}
                    label={t('MapModals.AddVul.comment')}
                    placeholder={t('MapModals.AddVul.commentPlaceholder')}
                    required
                  />
                )}
              />
            </div>
          </div>
          <div className="row mb-3">
            <Controller
              name="before_photos"
              control={control}
              render={({ field: { onChange } }) => (
                <MLInput
                  multiple
                  label={t('MapModals.AddVul.photo')}
                  type="file"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const files = e.target.files ? Array.from(e.target.files) : [];
                    onChange(files);
                  }}
                />
              )}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          {' '}
          <Button className="btn btn-ghost-danger" onClick={onReset}>
            {t('Modals.delete-modal.cancel')}
          </Button>
          <Button className="btn btn-ghost-success" onClick={handleSubmit(onSubmitForm)}>
            {t('TrapModals.confirm')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default AddVulOnMapModal;
